/**
 * Financial Health Check-Up PDF Generator
 * -------------------------------------------------------
 * Builds a clean, text-based A4 PDF directly with jsPDF (no html2canvas)
 * so the output is crisp and small (~30-50 KB) and renders consistently
 * across browsers and devices.
 *
 * The report includes:
 *   • Branded header with Arth Gurukul logo & date
 *   • Client + Advisor details
 *   • Result headline + score
 *   • Yes / No / NA totals
 *   • Risks & consequences
 *   • Full Q&A list with per-answer status pill
 *   • Disclaimer + advisor consultation CTA
 *   • Footer with page numbers on every page
 */
import jsPDF from 'jspdf';

const COLORS = {
  brand:     [212, 175,  55], // #D4AF37 — gold
  brandDark: [ 10,  25,  41], // #0A1929 — navy
  white:     [255, 255, 255],
  text:      [ 25,  25,  25],
  textLight: [110, 110, 110],
  red:       [220,  68,  68],
  amber:     [217, 119,   6],
  green:     [ 34, 159,  89],
  cyan:      [  6, 182, 212],
  border:    [225, 225, 225],
};

const RISK_COLOR = {
  critical: COLORS.red,
  medium:   COLORS.amber,
  low:      COLORS.green,
};

const fmtDate = (iso) => {
  if (!iso) return new Date().toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
  const d = new Date(iso);
  return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
};

/** ----- low-level helpers ----- */
const drawRect = (doc, x, y, w, h, color, fill = true) => {
  doc.setFillColor(...color);
  doc.setDrawColor(...color);
  doc.rect(x, y, w, h, fill ? 'F' : 'S');
};

const setText = (doc, color, size, style = 'normal') => {
  doc.setTextColor(...color);
  doc.setFontSize(size);
  doc.setFont('helvetica', style);
};

const drawPill = (doc, label, x, y, color) => {
  doc.setFillColor(...color);
  doc.setTextColor(...COLORS.white);
  doc.setFontSize(8);
  doc.setFont('helvetica', 'bold');
  const w = doc.getTextWidth(label) + 6;
  doc.roundedRect(x, y - 3.5, w, 5, 1.5, 1.5, 'F');
  doc.text(label, x + 3, y);
  return w;
};

/** ----- header + footer ----- */
const drawHeader = (doc) => {
  const pageW = doc.internal.pageSize.getWidth();
  drawRect(doc, 0, 0, pageW, 24, COLORS.brandDark);
  // Brand block
  setText(doc, COLORS.brand, 16, 'bold');
  doc.text('Arth Gurukul', 12, 12);
  setText(doc, COLORS.white, 8, 'normal');
  doc.text('Your School for Lifelong Financial Wellness', 12, 18);
  // Right side — report meta
  setText(doc, COLORS.white, 8, 'normal');
  doc.text('Financial Health Check-Up Report', pageW - 12, 12, { align: 'right' });
  setText(doc, COLORS.brand, 8, 'normal');
  doc.text(fmtDate(), pageW - 12, 18, { align: 'right' });
};

const drawFooter = (doc, pageNum, totalPages) => {
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  doc.setDrawColor(...COLORS.border);
  doc.line(12, pageH - 14, pageW - 12, pageH - 14);
  setText(doc, COLORS.textLight, 7, 'normal');
  doc.text('© 2026 Arth Gurukul', 12, pageH - 8);
  doc.text(`Page ${pageNum} of ${totalPages}`, pageW - 12, pageH - 8, { align: 'right' });
};

/** wrap-aware text rendering that auto-paginates */
const ensureRoom = (doc, cursorY, needed = 8) => {
  const pageH = doc.internal.pageSize.getHeight();
  if (cursorY + needed > pageH - 20) {
    doc.addPage();
    drawHeader(doc);
    return 32; // top margin after header
  }
  return cursorY;
};

const writeLine = (doc, text, x, y, opts = {}) => {
  const { color = COLORS.text, size = 10, style = 'normal' } = opts;
  setText(doc, color, size, style);
  doc.text(text, x, y);
};

const writeWrapped = (doc, text, x, y, maxW, opts = {}) => {
  const { color = COLORS.text, size = 10, style = 'normal', lineH = 4.5 } = opts;
  setText(doc, color, size, style);
  const lines = doc.splitTextToSize(text || '', maxW);
  lines.forEach((ln, i) => {
    const cy = ensureRoom(doc, y + i * lineH, lineH + 2);
    doc.text(ln, x, cy);
    if (cy !== y + i * lineH) y = cy - i * lineH; // page break happened
  });
  return y + lines.length * lineH;
};

const drawSectionTitle = (doc, title, y) => {
  y = ensureRoom(doc, y, 12);
  drawRect(doc, 12, y - 4, 3, 5, COLORS.brand);
  setText(doc, COLORS.text, 11, 'bold');
  doc.text(title.toUpperCase(), 18, y);
  return y + 6;
};

/** ----- main generator ----- */
export const generateFinancialHealthPDF = ({ result, client, advisor }) => {
  if (!result || !result.summary) {
    throw new Error('Missing financial-health result payload.');
  }

  const doc = new jsPDF('p', 'mm', 'a4');
  const pageW = doc.internal.pageSize.getWidth();
  const cw = pageW - 24; // content width inside 12mm horizontal padding
  const s = result.summary;
  const bandColor = RISK_COLOR[s.risk_band] || COLORS.amber;
  let y = 0;

  // ---------- Page 1: Header + Cover + Headline ----------
  drawHeader(doc);
  y = 36;

  // Cover block
  setText(doc, COLORS.textLight, 8, 'normal');
  doc.text('PERSONALISED FINANCIAL ASSESSMENT', 12, y);
  y += 6;
  setText(doc, COLORS.text, 20, 'bold');
  doc.text('Financial Health Check-Up Report', 12, y);
  y += 8;
  setText(doc, COLORS.textLight, 9, 'normal');
  doc.text(`Generated for ${client?.name || 'Client'} • ${fmtDate(result.created_at)}`, 12, y);
  y += 10;

  // -- Client & Advisor cards (side-by-side) --
  const cardH = 38;
  const cardW = (cw - 6) / 2;
  // Client card
  doc.setDrawColor(...COLORS.border);
  doc.roundedRect(12, y, cardW, cardH, 2, 2, 'S');
  drawRect(doc, 12, y, cardW, 7, COLORS.brandDark);
  setText(doc, COLORS.brand, 9, 'bold');
  doc.text('CLIENT', 15, y + 5);
  setText(doc, COLORS.text, 10, 'bold');
  doc.text(client?.name || '—', 15, y + 14);
  setText(doc, COLORS.textLight, 8, 'normal');
  doc.text(client?.email || '—', 15, y + 19);
  if (client?.phone) doc.text(client.phone, 15, y + 24);
  if (client?.city || client?.state) doc.text([client.city, client.state].filter(Boolean).join(', '), 15, y + 29);
  // Plan pill — empty/missing plan field is treated as PRO+ for legacy users
  // (matches AuthContext: isProPlan = plan === 'pro_plus' || !plan)
  const isPro = !client?.plan || client.plan === 'pro_plus';
  const planLabel = isPro ? 'PRO+ (Premium)' : 'Plus (Free)';
  drawPill(doc, planLabel, 15, y + cardH - 5, isPro ? COLORS.brand : COLORS.cyan);

  // Advisor card
  const ax = 12 + cardW + 6;
  doc.setDrawColor(...COLORS.border);
  doc.roundedRect(ax, y, cardW, cardH, 2, 2, 'S');
  drawRect(doc, ax, y, cardW, 7, COLORS.brandDark);
  setText(doc, COLORS.brand, 9, 'bold');
  doc.text('YOUR FINANCIAL CONSULTANT', ax + 3, y + 5);
  if (advisor) {
    setText(doc, COLORS.text, 10, 'bold');
    doc.text(advisor.name || '—', ax + 3, y + 14);
    if (advisor.designation) {
      setText(doc, COLORS.textLight, 8, 'normal');
      doc.text(advisor.designation, ax + 3, y + 19);
    }
    setText(doc, COLORS.textLight, 8, 'normal');
    let ay = advisor.designation ? 24 : 19;
    if (advisor.email)        { doc.text(advisor.email, ax + 3, y + ay); ay += 5; }
    if (advisor.phone)        { doc.text(advisor.phone, ax + 3, y + ay); ay += 5; }
    if (advisor.company_name) { doc.text(advisor.company_name, ax + 3, y + ay); }
  } else {
    setText(doc, COLORS.textLight, 9, 'italic');
    doc.text('No consultant assigned yet.', ax + 3, y + 16);
    setText(doc, COLORS.textLight, 8, 'normal');
    doc.text('Click "Talk to your consultant" in the app', ax + 3, y + 21);
    doc.text('to be connected with a qualified planner.', ax + 3, y + 26);
  }
  y += cardH + 8;

  // -- Risk headline --
  doc.setFillColor(...bandColor);
  doc.roundedRect(12, y, cw, 22, 2, 2, 'F');
  setText(doc, COLORS.white, 9, 'normal');
  doc.text('YOUR RESULT', 18, y + 7);
  setText(doc, COLORS.white, 14, 'bold');
  doc.text(s.risk_headline, 18, y + 16);
  y += 28;

  setText(doc, COLORS.text, 9, 'normal');
  // strip simple HTML entities from backend
  const interp = (s.interpretation || '').replace(/&amp;/g, '&').replace(/&[a-z]+;/g, ' ');
  y = writeWrapped(doc, interp, 12, y, cw, { color: COLORS.text, size: 9.5, lineH: 5 });
  y += 5;

  // -- Score panel — taller box so everything stays inside the rounded border --
  const scorePanelH = 44;
  y = ensureRoom(doc, y, scorePanelH + 4);
  doc.setDrawColor(...COLORS.border);
  doc.roundedRect(12, y, cw, scorePanelH, 2, 2, 'S');

  // Left half — title + score + percent + progress bar
  setText(doc, COLORS.textLight, 8, 'normal');
  doc.text('SCORE BREAKDOWN', 16, y + 8);

  setText(doc, COLORS.brand, 24, 'bold');
  doc.text(`${s.score}`, 16, y + 22);
  const scoreW = doc.getTextWidth(`${s.score}`);
  setText(doc, COLORS.textLight, 11, 'normal');
  doc.text(`/ ${s.max_score}`, 16 + scoreW + 2, y + 22);

  setText(doc, COLORS.textLight, 8, 'normal');
  doc.text(`${s.score_percent}% — each Yes earns 10 marks`, 16, y + 28);

  // progress bar
  const barX = 16;
  const barY = y + 32;
  const barW = 100;
  doc.setFillColor(225, 225, 225);
  doc.roundedRect(barX, barY, barW, 3, 1.5, 1.5, 'F');
  doc.setFillColor(...bandColor);
  const filledW = Math.max(1, Math.min(barW, (s.score_percent / 100) * barW));
  doc.roundedRect(barX, barY, filledW, 3, 1.5, 1.5, 'F');

  // Right half — 3 count tiles, fully contained inside the box vertically
  const tileH = 28;
  const tileW = 24;
  const tileGap = 3;
  const tileY = y + (scorePanelH - tileH) / 2; // vertically centered inside the box
  const tilesTotalW = tileW * 3 + tileGap * 2;
  const tileStartX = 12 + cw - tilesTotalW - 6; // 6mm right padding inside the box

  const drawCount = (idx, label, value, color) => {
    const cx = tileStartX + idx * (tileW + tileGap);
    doc.setDrawColor(...color);
    doc.roundedRect(cx, tileY, tileW, tileH, 2, 2, 'S');
    setText(doc, color, 16, 'bold');
    doc.text(`${value}`, cx + tileW / 2, tileY + 13, { align: 'center' });
    setText(doc, COLORS.textLight, 7, 'normal');
    doc.text(label.toUpperCase(), cx + tileW / 2, tileY + 22, { align: 'center' });
  };
  drawCount(0, 'Yes', s.yes_count, COLORS.green);
  drawCount(1, 'No',  s.no_count,  COLORS.red);
  drawCount(2, 'NA',  s.na_count,  COLORS.textLight);

  y += scorePanelH + 8;

  // ---------- Risks & Consequences ----------
  y = drawSectionTitle(doc, 'Risks & Consequences of Delay', y);
  setText(doc, COLORS.text, 9.5, 'normal');
  (s.consequences || []).forEach((c) => {
    y = ensureRoom(doc, y, 6);
    drawRect(doc, 14, y - 2, 1.5, 1.5, bandColor);
    y = writeWrapped(doc, c, 18, y, cw - 6, { color: COLORS.text, size: 9.5, lineH: 4.5 });
    y += 1;
  });
  y += 4;

  // ---------- Page 2+: Q&A ----------
  doc.addPage();
  drawHeader(doc);
  y = 32;
  y = drawSectionTitle(doc, 'Your Responses — Question by Question', y);
  setText(doc, COLORS.textLight, 8.5, 'italic');
  y = writeWrapped(doc, 'Each "Yes" earned 10 marks. The number of "No" answers determines your risk band.', 12, y, cw, { color: COLORS.textLight, size: 8.5, style: 'italic', lineH: 4 });
  y += 3;

  (result.detailed_answers || []).forEach((a) => {
    y = ensureRoom(doc, y, 16);
    // Question number circle
    doc.setFillColor(...COLORS.brand);
    doc.circle(15, y + 2, 3, 'F');
    setText(doc, COLORS.brandDark, 8, 'bold');
    doc.text(`${a.number}`, 15, y + 3.4, { align: 'center' });
    // Answer pill
    const pillColor = a.answer === 'yes' ? COLORS.green : a.answer === 'no' ? COLORS.red : COLORS.textLight;
    drawPill(doc, a.answer.toUpperCase(), 20, y + 2, pillColor);
    // Question text
    const qy = writeWrapped(doc, a.text, 36, y + 3, cw - 26, { color: COLORS.text, size: 9, lineH: 4.2 });
    y = Math.max(qy, y + 8);
    // divider
    doc.setDrawColor(...COLORS.border);
    doc.line(12, y, 12 + cw, y);
    y += 4;
  });

  y += 4;

  // ---------- Consultation CTA ----------
  y = ensureRoom(doc, y, 50);
  doc.setFillColor(255, 247, 219);
  doc.setDrawColor(...COLORS.brand);
  doc.roundedRect(12, y, cw, 38, 3, 3, 'FD');
  setText(doc, COLORS.brandDark, 12, 'bold');
  doc.text('Book a Personalised Consultation', 17, y + 9);
  setText(doc, COLORS.text, 9.5, 'normal');
  y = writeWrapped(
    doc,
    'Get a detailed Arth Kundli financial health review from a qualified consultant — tailored to your unique situation, goals and family. Talk to your consultant today; contact details are available in the app.',
    17, y + 16, cw - 10,
    { color: COLORS.text, size: 9, lineH: 4.5 }
  );
  y += 6;

  // ---------- Disclaimer ----------
  // Wrap the disclaimer inside its own rounded box with comfortable inside-padding,
  // and force a fresh page if there isn't enough clearance to render the box
  // without crashing into the footer.
  const disclaimerText = 'Disclaimer: The membership fee is for financial wellness education and financial wellness tools only. No fees are charged by the platform for any consultation or advice by a financial consultant or planner. This report is auto-generated based on self-reported answers and does not constitute financial, investment, legal or tax advice. Please consult a qualified professional before acting on it.';
  const discPadX = 5;
  const discPadY = 5;
  const discInnerW = cw - 2 * discPadX;
  // Calculate height needed at 7.5pt italic with 3.8mm line height
  setText(doc, COLORS.textLight, 7.5, 'italic');
  const discLines = doc.splitTextToSize(disclaimerText, discInnerW);
  const discBoxH = discPadY * 2 + discLines.length * 3.8;

  y = ensureRoom(doc, y, discBoxH + 6);
  doc.setDrawColor(...COLORS.border);
  doc.setFillColor(250, 250, 250);
  doc.roundedRect(12, y, cw, discBoxH, 2, 2, 'FD');
  // text inside
  setText(doc, COLORS.textLight, 7.5, 'italic');
  discLines.forEach((ln, i) => {
    doc.text(ln, 12 + discPadX, y + discPadY + (i + 1) * 3.8 - 1);
  });

  // ---------- Page numbers ----------
  const total = doc.internal.getNumberOfPages();
  for (let i = 1; i <= total; i++) {
    doc.setPage(i);
    drawFooter(doc, i, total);
  }

  // Save
  const fname = `Arth-Gurukul-Financial-Health-Report_${(client?.name || 'Client').replace(/\s+/g, '-')}_${new Date(result.created_at || Date.now()).toISOString().slice(0, 10)}.pdf`;
  doc.save(fname);
};

export default generateFinancialHealthPDF;
